import axios from 'axios';
import { FORCE_LOGOUT_EVENT } from '@/shared/providers/GlobalBehaviorWrapper';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { getRouter } from '@/shared/lib/routerRef';
import { APP_VERSION_MUTATION_SUCCESS_EVENT } from '@/shared/lib/appVersionEvents';
import {
  clearPortalSession,
  clearSsoSession,
  isSsoSite,
  readActivePortalAuthChannel,
  readActivePortalEntry,
  selectExactPortalBearerToken,
} from '@/features/sso/ssoSession';
import {
  activeSessionSyncContextFromRequest,
  captureActiveSessionAuthGeneration,
  isActiveSessionAuthGenerationCurrent,
  isActiveSessionSyncTransitionInFlight,
  type ActiveSessionAuthGenerationSnapshot,
} from '@/features/sso/activeSessionSync';
import {
  exactAuthorizationHeader,
  notifyTerminalSsoExpiry,
} from '@/features/sso/terminalSsoExpiry';

type GenerationBoundAxiosConfig = {
  __ra2nSsoAuthGeneration?: ActiveSessionAuthGenerationSnapshot;
};

/**
 * Shared Axios Instance
 * 
 * Centralized configuration for API requests.
 */
const API_BASE_URL = import.meta.env.VITE_API_URL ||
  (typeof window === 'undefined' ? undefined : window.location.origin);
const TOKEN_EXPIRED_MESSAGE = 'トークンが切れました。再度ログインしなおして操作してください。';
const ACCOUNT_DELETED_MESSAGE = '削除しました';
const SUPPRESS_FORCE_LOGOUT_TOAST_ONCE_KEY = 'suppress_force_logout_toast_once';

/** Parallel 401s after SERVICE_TYPE_MISMATCH: first response has error_code; follow-ups may be Sanctum 401 without it — dedupe toast for the same login redirect. */
const RAPID_LOGOUT_REDIRECT_DEDUPE_MS = 1200;
let lastLogoutRedirectAt = 0;
let lastLogoutRedirectPath = '';

const MUTATION_METHODS = new Set(['post', 'put', 'patch', 'delete']);

function shouldSuppressForceLogoutToast(): boolean {
  const suppressOnce = sessionStorage.getItem(SUPPRESS_FORCE_LOGOUT_TOAST_ONCE_KEY);
  if (suppressOnce === '1') {
    sessionStorage.removeItem(SUPPRESS_FORCE_LOGOUT_TOAST_ONCE_KEY);
    return true;
  }
  return false;
}

/** Client-side navigation keeps the shell mounted so the toast can render (avoids full reload). */
function dispatchForceLogoutToastAndNavigate(
  path: string,
  suppressToast = false,
  customMessage?: string,
): void {
  const now = Date.now();
  const rapidSameDestination =
    now - lastLogoutRedirectAt < RAPID_LOGOUT_REDIRECT_DEDUPE_MS &&
    lastLogoutRedirectPath === path;
  lastLogoutRedirectAt = now;
  lastLogoutRedirectPath = path;

  // If the user manually logged out, this will be true and consume the flag
  const suppressForManualLogout = shouldSuppressForceLogoutToast();

  const showToast =
    !rapidSameDestination &&
    !suppressToast &&
    !suppressForManualLogout;

  if (showToast) {
    window.dispatchEvent(
      new CustomEvent(FORCE_LOGOUT_EVENT, { detail: { message: customMessage ?? TOKEN_EXPIRED_MESSAGE } })
    );
  }
  getRouter().navigate(path, { replace: true });
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL ? `${API_BASE_URL}/api/v1` : undefined,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 100000,
});

// Completion hydration is cookie-authoritative. Keeping it interceptor-free avoids an old
// local bearer X winning over the freshly installed SSO cookie Y (or a failed probe clearing X).
export const ssoCookieClient = axios.create({
  baseURL: API_BASE_URL ? `${API_BASE_URL}/api/v1` : undefined,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 100000,
});

apiClient.interceptors.request.use(
  (config) => {
    const generationContext = activeSessionSyncContextFromRequest(
      config.url,
      window.location.pathname,
    );
    if (generationContext) {
      (config as typeof config & GenerationBoundAxiosConfig).__ra2nSsoAuthGeneration =
        captureActiveSessionAuthGeneration(generationContext, localStorage);
    }

    // A unified tenant publishes one credential channel regardless of the
    // principal model or the legacy business-controller prefix being called.
    const portalMatch = config.url?.match(/^\/portal\/([^/]+)/);
    const siteAdminMatch = config.url?.match(/^\/admin\/([^/]+)/);
    const massagerMatch = config.url?.match(/^\/massager\/([^/]+)/);
    const explicitSite = portalMatch?.[1] ?? siteAdminMatch?.[1] ?? massagerMatch?.[1];
    const explicitChannel = explicitSite && isSsoSite(explicitSite)
      ? readActivePortalAuthChannel(explicitSite, localStorage)
      : null;
    if (explicitChannel?.entryType === 'unified') {
      const token = selectExactPortalBearerToken(explicitChannel, localStorage);
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else if (portalMatch) {
      const token = selectExactPortalBearerToken(
        { site: portalMatch[1], entryType: 'unified' },
        localStorage,
      );
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else if (siteAdminMatch) {
      // Site Admin: /admin/{site}/...
      const token = selectExactPortalBearerToken(
        { site: siteAdminMatch[1], entryType: 'admin' },
        localStorage,
      );
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else if (config.url?.startsWith('/admin') && !config.url?.match(/^\/admin\/[^/]+/)) {
      // Full Admin: /admin/... (without site_type)
      const token = localStorage.getItem('admin_token');
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else if (massagerMatch) {
      // Massager (checkin) portal: /massager/{site}/...
      const token = localStorage.getItem('massager_token');
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else {
      const requestSite =
        config.url?.match(/^\/(?:clinic|portal)\/([^/]+)/)?.[1] ??
        window.location.pathname.match(/^\/([^/]+)(?:\/|$)/)?.[1];
      const activeEntry = requestSite && isSsoSite(requestSite)
        ? readActivePortalEntry(requestSite, localStorage)
        : null;
      // An active admin entry only means admin logged in last. A clinic page keeps using the
      // clinic credential (HIT-15654); an admin page must not replay it on a clinic-prefixed API.
      const isAdminPage = /^\/[^/]+\/admin(\/|$)/.test(window.location.pathname);
      const token = activeEntry
        ? activeEntry === 'admin' && isAdminPage
          ? null
          : selectExactPortalBearerToken(
              {
                site: requestSite as string,
                entryType: activeEntry === 'unified' ? 'unified' : 'clinic',
              },
              localStorage
            )
        : localStorage.getItem('auth_token');
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // Clinic portal APIs under /clinic/{site}/{clinic}/... require X-Service-Type when clinic is single-service (middleware).
      const url = config.url ?? '';
      if (
        url.startsWith('/clinic/') &&
        !/^\/clinic\/[^/]+\/auth(\/|$)/.test(url)
      ) {
        if (!config.headers['X-Service-Type']) {
          const raw = localStorage.getItem('service_type') || SERVICE_TYPE_R2;
          const u = raw.trim().toUpperCase();
          config.headers['X-Service-Type'] = u === SERVICE_TYPE_A2 ? SERVICE_TYPE_A2 : SERVICE_TYPE_R2;
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor
apiClient.interceptors.response.use(
  (response) => {
    const method = response.config.method?.toLowerCase();
    if (method && MUTATION_METHODS.has(method)) {
      window.dispatchEvent(new CustomEvent(APP_VERSION_MUTATION_SUCCESS_EVENT));
    }
    return response;
  },
  (error) => {
    // Handle 401 errors
    if (error.response?.status === 401) {
      const generationSnapshot = (
        error.config as GenerationBoundAxiosConfig | undefined
      )?.__ra2nSsoAuthGeneration;
      if (
        generationSnapshot &&
        !isActiveSessionAuthGenerationCurrent(generationSnapshot, localStorage)
      ) {
        return Promise.reject(error);
      }
      if (isActiveSessionSyncTransitionInFlight()) {
        return Promise.reject(error);
      }
      // Don't redirect if this is a login request
      const isLoginRequest =
        error.config?.url?.includes('/auth/login') ||
        error.config?.url?.match(/^\/admin\/[^/]+\/login/) ||
        error.config?.url?.match(/^\/massager\/[^/]+\/auth\/login/);
      const isLogoutRequest =
        error.config?.url?.includes('/auth/logout') ||
        error.config?.url?.match(/^\/admin\/[^/]+\/logout/) ||
        error.config?.url?.match(/^\/massager\/[^/]+\/auth\/logout/);

      if (!isLoginRequest && !isLogoutRequest) {
        if (generationSnapshot) {
          void notifyTerminalSsoExpiry({
            context: generationSnapshot.context,
            generation: generationSnapshot,
            authorization: exactAuthorizationHeader(error.config?.headers),
            localStorage,
          });
        }
        const errorCode =
          (error.response?.data as { error_code?: string } | undefined)?.error_code;
        const suppressToastForServiceTypeMismatch =
          errorCode === 'SERVICE_TYPE_MISMATCH';
        const isAccountDeleted = errorCode === 'ACCOUNT_DELETED';

        const toastMessage = isAccountDeleted ? ACCOUNT_DELETED_MESSAGE : undefined;

        if (generationSnapshot?.context.entryType === 'unified') {
          const context = generationSnapshot.context;
          clearPortalSession(context, localStorage, sessionStorage);
          dispatchForceLogoutToastAndNavigate(
            `/${context.site}/login`,
            suppressToastForServiceTypeMismatch,
            toastMessage,
          );
          return Promise.reject(error);
        }

        // If the failing request is for Site Admin, redirect to the Site Admin login
        // instead of the clinic (tenant) login.
        const siteAdminMatch = error.config?.url?.match(/^\/admin\/([^/]+)\b/);
        if (siteAdminMatch?.[1]) {
          const site = siteAdminMatch[1];

          localStorage.removeItem('site_admin_token');
          localStorage.removeItem('site_admin');
          localStorage.removeItem('site_admin_site');
          if (isSsoSite(site)) {
            clearSsoSession({ site, entryType: 'admin' });
          }

          dispatchForceLogoutToastAndNavigate(`/${site}/admin/login`, suppressToastForServiceTypeMismatch, toastMessage);
          return Promise.reject(error);
        }

        // Full Admin (no site_type in URL): clear admin storage and return to root (admin login).
        if (error.config?.url?.startsWith('/admin')) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin');
          dispatchForceLogoutToastAndNavigate('/', suppressToastForServiceTypeMismatch, toastMessage);
          return Promise.reject(error);
        }

        // Massager portal: 401 from /massager/{site}/... → clear massager storage and return to massager login.
        const massagerMatch = error.config?.url?.match(/^\/massager\/([^/]+)\b/);
        if (massagerMatch?.[1]) {
          const rawSite = massagerMatch[1];
          // upsystem and medix are merged — keep massager users on the medix login.
          const site = rawSite.toLowerCase().includes('upsystem') ? 'medix' : rawSite;

          localStorage.removeItem('massager_token');
          localStorage.removeItem('massager_user');
          localStorage.removeItem('massager_clinic');
          localStorage.removeItem('massager_site');
          dispatchForceLogoutToastAndNavigate(
            `/${site}/massager/login`,
            suppressToastForServiceTypeMismatch,
            toastMessage
          );
          return Promise.reject(error);
        }

        // If the user is currently on a massager URL but the failing request wasn't /massager/...,
        // still keep them in the massager portal by redirecting to the massager login.
        const currentPathname = window.location.pathname;
        const currentMassagerMatch = currentPathname.match(/^\/([^/]+)\/massager(?:\/|$)/);
        if (currentMassagerMatch?.[1]) {
          const rawSite = currentMassagerMatch[1];
          const site = rawSite.toLowerCase().includes('upsystem') ? 'medix' : rawSite;

          localStorage.removeItem('massager_token');
          localStorage.removeItem('massager_user');
          localStorage.removeItem('massager_clinic');
          localStorage.removeItem('massager_site');
          dispatchForceLogoutToastAndNavigate(
            `/${site}/massager/login`,
            suppressToastForServiceTypeMismatch,
            toastMessage
          );
          return Promise.reject(error);
        }

        // If the user is currently in Site Admin portal, keep them in Site Admin portal
        // even if the failing request is not a /{site}/admin/... URL.
        const currentSiteAdminMatch = currentPathname.match(/^\/([^/]+)\/admin(?:\/|$)/);
        if (currentSiteAdminMatch?.[1]) {
          const site = currentSiteAdminMatch[1];

          localStorage.removeItem('site_admin_token');
          localStorage.removeItem('site_admin');
          localStorage.removeItem('site_admin_site');
          if (isSsoSite(site)) {
            clearSsoSession({ site, entryType: 'admin' });
          }

          dispatchForceLogoutToastAndNavigate(`/${site}/admin/login`, suppressToastForServiceTypeMismatch, toastMessage);
          return Promise.reject(error);
        }

        // Default: clinic portal redirect logic
        // Derive site from current URL first to avoid hardcoding a default.
        const currentPath = window.location.pathname;
        const currentSiteMatch = currentPath.match(/^\/([^/]+)(?:\/|$)/);

        const clinicStr = localStorage.getItem('auth_clinic');
        const clinic = clinicStr ? JSON.parse(clinicStr) : null;
        const siteType =
          currentSiteMatch?.[1] || clinic?.parent_site_type || clinic?.site_type || 'medix';

        // Clear storage
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_clinic');
        if (isSsoSite(siteType)) {
          const activeEntry = readActivePortalEntry(siteType, localStorage);
          clearSsoSession({
            site: siteType,
            entryType: activeEntry === 'unified' ? 'unified' : 'clinic',
          });
        }

        dispatchForceLogoutToastAndNavigate(`/${siteType}/login`, suppressToastForServiceTypeMismatch, toastMessage);
      }
    }
    return Promise.reject(error);
  }
);
